import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { getCuentas, createCuenta, updateCuenta, deleteCuenta } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, Landmark } from 'lucide-react';

const TIPOS_CUENTA = ['BANCO', 'CAJA', 'TARJETA', 'OTRO'];

const CuentasPage = () => {
  const { empresaActual, monedas } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', tipo: 'BANCO', moneda_id: '', saldo_inicial_me: 0, activo: true });

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await getCuentas(empresaActual.id);
      setItems(res.data);
    } catch (error) {
      toast.error('Error al cargar cuentas');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (monedas.length > 0 && !formData.moneda_id) {
      const pen = monedas.find(m => m.codigo === 'PEN');
      if (pen) setFormData(prev => ({ ...prev, moneda_id: pen.id.toString() }));
    }
  }, [monedas, formData.moneda_id]);

  const resetForm = () => {
    const pen = monedas.find(m => m.codigo === 'PEN');
    setFormData({ nombre: '', tipo: 'BANCO', moneda_id: pen?.id?.toString() || '', saldo_inicial_me: 0, activo: true });
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ nombre: item.nombre, tipo: item.tipo, moneda_id: item.moneda_id?.toString() || '', saldo_inicial_me: item.saldo_inicial_me, activo: item.activo });
    } else { resetForm(); }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre) { toast.error('El nombre es requerido'); return; }
    const payload = { ...formData, empresa_id: empresaActual.id, moneda_id: parseInt(formData.moneda_id), saldo_inicial_me: parseFloat(formData.saldo_inicial_me) || 0 };
    try {
      if (editingItem) { await updateCuenta(editingItem.id, payload); toast.success('Cuenta actualizada'); }
      else { await createCuenta(payload); toast.success('Cuenta creada'); }
      setDialogOpen(false); resetForm(); loadData();
    } catch (error) { toast.error('Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta cuenta?')) return;
    try { await deleteCuenta(id); toast.success('Cuenta eliminada'); loadData(); }
    catch (error) { toast.error('Error al eliminar'); }
  };

  if (!empresaActual) return <div className="text-center py-12 text-gray-500">Selecciona una empresa</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Landmark className="w-8 h-8 text-emerald-600" />
          <h1 className="text-2xl font-bold text-gray-900">Cuentas Bancarias</h1>
        </div>
        <Button onClick={() => handleOpenDialog()}><Plus className="w-4 h-4 mr-2" />Nueva Cuenta</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No hay cuentas</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Moneda</TableHead>
                  <TableHead className="text-right">Saldo Actual</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.nombre}</TableCell>
                    <TableCell>{item.tipo}</TableCell>
                    <TableCell>{item.moneda_codigo}</TableCell>
                    <TableCell className={`text-right font-bold ${item.saldo_actual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {item.moneda_codigo === 'USD' ? '$' : 'S/'} {item.saldo_actual?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell><Badge className={item.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>{item.activo ? 'Activo' : 'Inactivo'}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingItem ? 'Editar' : 'Nueva'} Cuenta</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Nombre *</Label><Input value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPOS_CUENTA.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Moneda</Label>
                <Select value={formData.moneda_id} onValueChange={(v) => setFormData({ ...formData, moneda_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{monedas.map(m => <SelectItem key={m.id} value={m.id.toString()}>{m.codigo}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Saldo Inicial</Label><Input type="number" step="0.01" value={formData.saldo_inicial_me} onChange={(e) => setFormData({ ...formData, saldo_inicial_me: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CuentasPage;
